import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SearchableSelect from '../components/SearchableSelect';
import { Plus, Trash, Pencil, Printer, FileText, CurrencyInr, Download, CopySimple, ArrowsClockwise, EnvelopeSimple, WhatsappLogo } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toDDMMYYYY, todayISO, addDaysISO } from '../lib/dateUtils';
import { loadCompanies, buildUpiLink } from '../lib/companies';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLOR = {
  paid: { bg: '#dcfce7', color: '#166534' },
  unpaid: { bg: '#fee2e2', color: '#991b1b' },
  partial: { bg: '#fef9c3', color: '#854d0e' },
  overdue: { bg: '#fce7f3', color: '#9d174d' },
  draft: { bg: '#f3f4f6', color: '#374151' },
};

const emptyItem = () => ({ description: '', details: '', qty: 1, unit_price: 0, tax_percentage: 0, amount: 0, hsn_sac: '' });
const emptyInv = () => ({
  id: '', invoice_number: '', company_id: 'dextra_tech', client_id: '',
  invoice_date: todayISO(), due_date: addDaysISO(todayISO(), 7), status: 'unpaid',
  currency: 'INR',
  items: [emptyItem()], discount_value: 0, discount_type: 'amount',
  tax_percentage: 0, invoice_taxes: [], notes: '', terms: '', payment_mode: 'NEFT',
  place_of_supply: '', paid_amount: 0,
  project_id: '',
});

// Supported currencies — flag, symbol, locale for number formatting
export const CURRENCIES = [
  { code: 'INR', symbol: '₹',  flag: '🇮🇳', name: 'Indian Rupee',      locale: 'en-IN', words: 'Indian Rupees',     subunit: 'Paise',  numbering: 'indian' },
  { code: 'USD', symbol: '$',  flag: '🇺🇸', name: 'US Dollar',          locale: 'en-US', words: 'US Dollars',        subunit: 'Cents',  numbering: 'western' },
  { code: 'SGD', symbol: 'S$', flag: '🇸🇬', name: 'Singapore Dollar',   locale: 'en-SG', words: 'Singapore Dollars', subunit: 'Cents',  numbering: 'western' },
  { code: 'MYR', symbol: 'RM', flag: '🇲🇾', name: 'Malaysian Ringgit',  locale: 'en-MY', words: 'Malaysian Ringgit', subunit: 'Sen',    numbering: 'western' },
  { code: 'AUD', symbol: 'A$', flag: '🇦🇺', name: 'Australian Dollar',  locale: 'en-AU', words: 'Australian Dollars',subunit: 'Cents',  numbering: 'western' },
  { code: 'EUR', symbol: '€',  flag: '🇪🇺', name: 'Euro',               locale: 'en-IE', words: 'Euros',             subunit: 'Cents',  numbering: 'western' },
  { code: 'GBP', symbol: '£',  flag: '🇬🇧', name: 'British Pound',      locale: 'en-GB', words: 'Pounds Sterling',   subunit: 'Pence',  numbering: 'western' },
  { code: 'AED', symbol: 'AED', flag: '🇦🇪', name: 'UAE Dirham',        locale: 'en-AE', words: 'UAE Dirhams',       subunit: 'Fils',   numbering: 'western' },
  { code: 'SAR', symbol: 'SAR', flag: '🇸🇦', name: 'Saudi Riyal',       locale: 'en-SA', words: 'Saudi Riyals',      subunit: 'Halalas',numbering: 'western' },
];
export const CCY = Object.fromEntries(CURRENCIES.map((c) => [c.code, c]));
const fmtCcy = (n, code = 'INR') => {
  const c = CCY[code] || CCY.INR;
  return c.symbol + Number(n || 0).toLocaleString(c.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const numberToIndianWords = (num) => {
  if (num === 0) return 'Zero';
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const inW = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
    if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + inW(n % 100) : '');
    return '';
  };
  let n = Math.round(num); let out = '';
  const cr = Math.floor(n / 10000000); n %= 10000000;
  const lk = Math.floor(n / 100000); n %= 100000;
  const th = Math.floor(n / 1000); n %= 1000;
  if (cr) out += inW(cr) + ' Crore ';
  if (lk) out += inW(lk) + ' Lakh ';
  if (th) out += inW(th) + ' Thousand ';
  if (n) out += inW(n);
  return out.trim();
};

// Western (international) number to words — billions/millions/thousands grouping.
const numberToWesternWords = (num) => {
  if (num === 0) return 'Zero';
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const inW = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? '-' + a[n % 10] : '');
    if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + inW(n % 100) : '');
    return '';
  };
  let n = Math.round(num); let out = '';
  const bn = Math.floor(n / 1000000000); n %= 1000000000;
  const mn = Math.floor(n / 1000000); n %= 1000000;
  const th = Math.floor(n / 1000); n %= 1000;
  if (bn) out += inW(bn) + ' Billion ';
  if (mn) out += inW(mn) + ' Million ';
  if (th) out += inW(th) + ' Thousand ';
  if (n) out += inW(n);
  return out.trim();
};

// Format full amount in words including currency name and subunit (e.g. "US Dollars One Hundred Only" or
// "Indian Rupees One Thousand and Fifty Paise Only").
const amountInWords = (amount, currencyCode = 'INR') => {
  const c = CCY[currencyCode] || CCY.INR;
  const main = Math.floor(amount);
  const sub = Math.round((amount - main) * 100);
  const inW = c.numbering === 'indian' ? numberToIndianWords : numberToWesternWords;
  let txt = `${c.words} ${inW(main) || 'Zero'}`;
  if (sub > 0) txt += ` and ${inW(sub)} ${c.subunit}`;
  txt += ' Only';
  return txt;
};

export const InvoicesPage = () => {
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectBilling, setProjectBilling] = useState(null);  // billing summary for form.project_id
  const [products, setProducts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(emptyInv());

  const [companies, setCompanies] = useState([]);
  const [clientGstFilter, setClientGstFilter] = useState('all'); // all | gst | nongst
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);  // [] = all; else ['unpaid','partial',...] — multi-select
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [companyTab, setCompanyTab] = useState('all'); // all | <company_id>
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [payOpen, setPayOpen] = useState(false);
  // received_amount = what actually hits your bank. tds_amount auto-derived from invoice's TAXABLE BASE × tds% (Indian rule).
  // gross_settled (sent to backend as `amount`) = received_amount + tds_amount.
  const [payForm, setPayForm] = useState({ invoice_id: '', received_amount: 0, paid_date: todayISO(), paid_via: 'bank', paid_account_id: '', paid_account: '', paid_reference: '', paid_notes: '', tds_percentage: 0, tds_section: '', inr_received: 0 });
  const [invoicePayments, setInvoicePayments] = useState([]);
  const [paymentsForInv, setPaymentsForInv] = useState(null);  // {invoice} currently shown
  const [bankAccounts, setBankAccounts] = useState([]);

  // Invoice base/taxable value (pre-GST) used for TDS calculation. TDS in India is
  // computed on the **taxable value**, NOT on the gross including GST.
  const invoiceTaxableBase = (inv) => {
    if (!inv) return 0;
    if (inv.sub_total != null) return Math.max(0, Number(inv.sub_total || 0) - Number(inv.discount_amount || 0));
    return Math.max(0, Number(inv.total || 0) - Number(inv.tax_amount || 0));
  };

  const openPayments = async (inv) => {
    setPaymentsForInv(inv);
    const outstanding = Math.max(0, (inv.total || 0) - (inv.paid_amount || 0));
    setPayForm({
      invoice_id: inv.id,
      received_amount: outstanding,            // default: settle full outstanding
      paid_date: todayISO(),
      paid_via: 'bank',
      paid_account_id: '',
      paid_account: '',
      paid_reference: '',
      paid_notes: '',
      tds_percentage: 0,
      tds_section: '',
      // Foreign-currency invoices: pre-fill the expected INR receipt using the invoice's stored FX rate.
      inr_received: (inv.currency && inv.currency !== 'INR')
        ? Math.round(outstanding * Number(inv.fx_rate_inr || 1) * 100) / 100
        : 0,
    });
    // History comes from the invoice's `receipts` array (new flow) — fall back to /api/payments for legacy
    setInvoicePayments(inv.receipts || []);
    // Lazy-load active bank accounts for the dropdown
    if (bankAccounts.length === 0) {
      axios.get(`${API_URL}/api/bank-accounts`, { withCredentials: true })
        .then((r) => setBankAccounts(r.data || []))
        .catch(() => setBankAccounts([]));
    }
    setPayOpen(true);
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    const received = parseFloat(payForm.received_amount) || 0;
    if (received <= 0) { alert('Amount received must be greater than 0'); return; }
    const tdsPct = parseFloat(payForm.tds_percentage) || 0;
    const base = invoiceTaxableBase(paymentsForInv);
    // TDS is computed on the invoice's taxable base (pre-GST). For a fully-settled invoice this
    // is the standard ₹ value the client deducted. For a partial payment with TDS, the user can
    // still edit `received_amount` and the gross_settled will be received + tds.
    const tdsAmt = tdsPct > 0 ? Math.round(base * tdsPct) / 100 : 0;
    const grossSettled = Math.round((received + tdsAmt) * 100) / 100;
    const outstanding = Math.max(0, (paymentsForInv.total || 0) - (paymentsForInv.paid_amount || 0));
    if (grossSettled > outstanding + 0.005) {
      if (!window.confirm(`Gross settled (₹${grossSettled.toFixed(2)}) exceeds outstanding (₹${outstanding.toFixed(2)}). Continue anyway?`)) return;
    }
    try {
      const { data: updated } = await axios.post(
        `${API_URL}/api/invoices/${payForm.invoice_id}/payment`,
        {
          amount: grossSettled,
          paid_via: payForm.paid_via,
          paid_account_id: payForm.paid_account_id || '',
          paid_account: payForm.paid_account || '',
          paid_reference: payForm.paid_reference || '',
          paid_date: payForm.paid_date,
          paid_notes: payForm.paid_notes || '',
          tds_percentage: tdsPct,
          tds_amount: tdsAmt,
          tds_section: payForm.tds_section || '',
          inr_received: parseFloat(payForm.inr_received) || 0,
        },
        { withCredentials: true }
      );
      setPaymentsForInv(updated);
      setInvoicePayments(updated.receipts || []);
      axios.get(`${API_URL}/api/bank-accounts`, { withCredentials: true })
        .then((r) => setBankAccounts(r.data || [])).catch(() => {});
      await load();
      // Rehydrate the form with the NEW outstanding balance so the FX panel keeps making
      // sense if the admin wants to log a second (partial) payment on the same invoice.
      const newOutstanding = Math.max(0, Number(updated.total || 0) - Number(updated.paid_amount || 0));
      setPayForm((f) => ({
        ...f,
        received_amount: newOutstanding,
        paid_reference: '',
        paid_notes: '',
        tds_percentage: 0,
        tds_section: '',
        inr_received: (updated.currency && updated.currency !== 'INR')
          ? Math.round(newOutstanding * Number(updated.fx_rate_inr || 1) * 100) / 100
          : 0,
      }));
    } catch (err) {
      alert('Save failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const deletePayment = async (id) => {
    if (!window.confirm('Reverse this payment? Linked ledger entry will roll back too.')) return;
    try {
      const { data: updated } = await axios.post(
        `${API_URL}/api/invoices/${paymentsForInv.id}/payments/${id}/reverse`,
        {},
        { withCredentials: true }
      );
      setPaymentsForInv(updated);
      setInvoicePayments(updated.receipts || []);
      // Rehydrate the payment form with the fresh outstanding balance so the FX panel
      // and Amount Received field reflect what's still due after the reversal.
      const newOutstanding = Math.max(0, Number(updated.total || 0) - Number(updated.paid_amount || 0));
      setPayForm((f) => ({
        ...f,
        received_amount: newOutstanding,
        inr_received: (updated.currency && updated.currency !== 'INR')
          ? Math.round(newOutstanding * Number(updated.fx_rate_inr || 1) * 100) / 100
          : 0,
      }));
      axios.get(`${API_URL}/api/bank-accounts`, { withCredentials: true })
        .then((r) => setBankAccounts(r.data || [])).catch(() => {});
      await load();
    } catch (err) { alert('Reverse failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const load = async () => {
    try {
      // Include written-off invoices so they show up when the user filters "Written Off"
      const [i, c, p, t, pr] = await Promise.all([
        axios.get(`${API_URL}/api/invoices?include_written_off=true`, { withCredentials: true }),
        axios.get(`${API_URL}/api/clients`, { withCredentials: true }),
        axios.get(`${API_URL}/api/products`, { withCredentials: true }),
        axios.get(`${API_URL}/api/taxes`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setList(i.data); setClients(c.data); setProducts(p.data); setTaxes(t.data);
      setProjects(pr.data || []);
      const cos = await loadCompanies();
      setCompanies(cos);
      if (cos.length && !cos.find((co) => co.id === form.company_id)) {
        setForm((f) => ({ ...f, company_id: cos[0].id }));
      }
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // When project_id changes, fetch billing summary so we can show budget vs remaining inline
  useEffect(() => {
    if (!form.project_id) { setProjectBilling(null); return; }
    axios.get(`${API_URL}/api/projects/${form.project_id}/billing`, { withCredentials: true })
      .then((r) => setProjectBilling(r.data))
      .catch(() => setProjectBilling(null));
  }, [form.project_id]);

  const companyById = useMemo(() => Object.fromEntries(companies.map((c) => [c.id, c])), [companies]);
  const getCo = (id) => companyById[id] || companies[0] || { short: 'CO', gst_enabled: false, bank: {} };

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);

  // Filtered + selectable list (shared by table render + bulk PDF export)
  // Sorted newest first (descending invoice_date, then invoice_number desc).
  const filteredInvoices = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out = list.filter((inv) => {
      if (companyTab !== 'all' && inv.company_id !== companyTab) return false;
      // Hide written-off invoices from all views unless explicitly filtered for them
      if (statusFilter.length === 0 && inv.status === 'written_off') return false;
      if (statusFilter.length > 0 && !statusFilter.includes(inv.status)) return false;
      if (currencyFilter !== 'all' && (inv.currency || 'INR') !== currencyFilter) return false;
      if (dateFrom && (inv.invoice_date || '') < dateFrom) return false;
      if (dateTo && (inv.invoice_date || '') > dateTo) return false;
      if (!q) return true;
      const c = clientMap[inv.client_id];
      const co = getCo(inv.company_id);
      const blob = [
        inv.invoice_number, co?.name, co?.short,
        c?.first_name, c?.last_name, c?.company_name, c?.email,
        inv.notes, inv.payment_mode, inv.currency,
      ].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });
    // Newest invoices first: by invoice_date desc, then invoice_number desc as tie-breaker.
    out.sort((a, b) => {
      const da = a.invoice_date || '';
      const db = b.invoice_date || '';
      if (da !== db) return db.localeCompare(da);
      return String(b.invoice_number || '').localeCompare(String(a.invoice_number || ''));
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, search, statusFilter, currencyFilter, companyTab, dateFrom, dateTo, clientMap, companies]);

  // Keep selection valid when list/filters change
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set(filteredInvoices.map((i) => i.id));
      const next = new Set();
      prev.forEach((id) => { if (validIds.has(id)) next.add(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [filteredInvoices]);

  // Reset to page 1 when filters narrow the result set
  useEffect(() => { setPage(1); }, [search, statusFilter, currencyFilter, companyTab, dateFrom, dateTo, pageSize]);

  // Pagination math
  const totalCount = filteredInvoices.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, totalCount);
  const pagedInvoices = useMemo(
    () => filteredInvoices.slice(startIdx, endIdx),
    [filteredInvoices, startIdx, endIdx],
  );

  // Aggregate totals across ALL filtered invoices, converted to INR using each invoice's
  // stored `fx_rate_inr` (defaults to 1.0 for INR invoices). Displayed as a summary strip
  // just above the pagination footer so the admin doesn't have to open Invoice Reports.
  const filterTotals = useMemo(() => {
    let invoiced = 0, paid = 0, outstanding = 0;
    const mixedCurrencies = new Set();
    for (const inv of filteredInvoices) {
      const rate = Number(inv.fx_rate_inr || 1) || 1;
      const total = Number(inv.total || 0);
      const p = Number(inv.paid_amount || 0);
      invoiced += total * rate;
      paid += p * rate;
      outstanding += Math.max(0, total - p) * rate;
      mixedCurrencies.add(inv.currency || 'INR');
    }
    return {
      invoiced_inr: invoiced,
      paid_inr: paid,
      outstanding_inr: outstanding,
      count: filteredInvoices.length,
      mixed: mixedCurrencies.size > 1,
      currencies: Array.from(mixedCurrencies),
    };
  }, [filteredInvoices]);

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds((prev) => {
      const allIds = pagedInvoices.map((i) => i.id);
      const allSelected = allIds.length > 0 && allIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        allIds.forEach((id) => next.delete(id));
      } else {
        allIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // Totals preview (mirrors backend)
  const totals = useMemo(() => {
    let sub = 0, lineTax = 0;
    (form.items || []).forEach((it) => {
      const line = (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0);
      sub += line;
      lineTax += line * (parseFloat(it.tax_percentage) || 0) / 100;
    });
    const disc = form.discount_type === 'percentage'
      ? sub * (parseFloat(form.discount_value) || 0) / 100
      : (parseFloat(form.discount_value) || 0);
    const taxable = sub - disc;
    let invTax = 0;
    (form.invoice_taxes || []).forEach((t) => {
      invTax += taxable * (parseFloat(t.percentage) || 0) / 100;
    });
    if (invTax === 0 && lineTax === 0 && form.tax_percentage) {
      invTax = taxable * parseFloat(form.tax_percentage) / 100;
    }
    const tax = lineTax + invTax;
    return { sub, tax, disc, total: taxable + tax };
  }, [form]);

  const addInvoiceTax = (taxId) => {
    if (!taxId) return;
    const t = taxes.find((x) => x.id === taxId);
    if (!t) return;
    if ((form.invoice_taxes || []).some((x) => x.id === t.id)) return;
    setForm({ ...form, invoice_taxes: [...(form.invoice_taxes || []), { id: t.id, name: t.name, percentage: t.percentage }] });
  };
  const removeInvoiceTax = (taxId) => {
    setForm({ ...form, invoice_taxes: (form.invoice_taxes || []).filter((x) => x.id !== taxId) });
  };

  const setItem = (i, key, val) => {
    const items = form.items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it));
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, emptyItem()] });
  const delItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) { alert('Please select a client'); return; }
    if (!form.items.length) { alert('Add at least one line item'); return; }
    const payload = {
      ...form,
      discount_value: parseFloat(form.discount_value) || 0,
      tax_percentage: parseFloat(form.tax_percentage) || 0,
      paid_amount: parseFloat(form.paid_amount) || 0,
      fx_rate_inr: parseFloat(form.fx_rate_inr) || (form.currency === 'INR' ? 1 : 1),
      invoice_taxes: (form.invoice_taxes || []).map((t) => ({
        id: t.id || '', name: t.name, percentage: parseFloat(t.percentage) || 0,
      })),
      items: form.items.map((it) => ({
        description: it.description,
        details: it.details || '',            // ← multi-line details (rendered under heading on PDF)
        qty: parseFloat(it.qty) || 0,
        unit_price: parseFloat(it.unit_price) || 0,
        tax_percentage: parseFloat(it.tax_percentage) || 0,
        hsn_sac: it.hsn_sac || '',
        amount: 0,
      })),
    };
    try {
      if (edit) await axios.put(`${API_URL}/api/invoices/${form.id}`, payload, { withCredentials: true });
      else await axios.post(`${API_URL}/api/invoices`, payload, { withCredentials: true });
      setOpen(false); setForm(emptyInv()); setEdit(false); load();
    } catch (err) { alert('Save failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const onEdit = (inv) => { setForm({ ...emptyInv(), ...inv, invoice_taxes: inv.invoice_taxes || [], items: inv.items?.length ? inv.items : [emptyItem()] }); setEdit(true); setOpen(true); };
  const onDel = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    await axios.delete(`${API_URL}/api/invoices/${id}`, { withCredentials: true });
    load();
  };

  // ── Write-off state (absconded client / bad debt) ─────────────────────────
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [writeOffInv, setWriteOffInv] = useState(null);
  const [writeOffReason, setWriteOffReason] = useState('absconded');
  const [writeOffNote, setWriteOffNote] = useState('');
  const [writeOffBusy, setWriteOffBusy] = useState(false);

  const openWriteOff = (inv) => {
    setWriteOffInv(inv);
    setWriteOffReason('absconded');
    setWriteOffNote('');
    setWriteOffOpen(true);
  };

  const submitWriteOff = async () => {
    if (!writeOffInv) return;
    setWriteOffBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/invoices/${writeOffInv.id}/write-off`,
        { reason: writeOffReason, note: writeOffNote },
        { withCredentials: true },
      );
      toast.success(`Invoice ${writeOffInv.invoice_number || ''} written off`);
      setWriteOffOpen(false);
      await load();
    } catch (e) {
      toast.error('Failed: ' + (e.response?.data?.detail || e.message));
    }
    setWriteOffBusy(false);
  };

  const restoreWriteOff = async (inv) => {
    if (!window.confirm(`Restore invoice ${inv.invoice_number || ''} — it will re-appear in the receivables list.`)) return;
    try {
      await axios.post(`${API_URL}/api/invoices/${inv.id}/restore`, {}, { withCredentials: true });
      toast.success('Restored');
      await load();
    } catch (e) {
      toast.error('Failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Clone an existing invoice as a fresh DRAFT with a new auto-generated number.
  // The dialog opens on the new invoice so the admin can tweak amounts / dates and save.
  const onClone = async (inv) => {
    if (!window.confirm(`Clone invoice ${inv.invoice_number || ''} as a new draft? A fresh invoice number will be auto-generated (editable).`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/invoices/${inv.id}/clone`, {}, { withCredentials: true });
      toast.success(`Cloned as ${r.data?.invoice_number || 'new invoice'}`);
      await load();
      // Immediately open the newly created invoice in edit mode so the admin can tweak & save
      const created = r.data;
      if (created) {
        setForm({ ...emptyInv(), ...created, invoice_taxes: created.invoice_taxes || [], items: created.items?.length ? created.items : [emptyItem()] });
        setEdit(true);
        setOpen(true);
      }
    } catch (e) {
      toast.error('Clone failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Manual delivery tracking — admins flip these AFTER sending via Outlook / WhatsApp Business
  // so the team has a single source of truth about which invoices are already out.
  const markSent = async (inv, channel, sent) => {
    try {
      const r = await axios.post(
        `${API_URL}/api/invoices/${inv.id}/mark-sent`,
        { channel, sent },
        { withCredentials: true },
      );
      // Optimistic in-place update so the row re-renders immediately
      setList((prev) => prev.map((x) => x.id === inv.id ? { ...x, ...r.data } : x));
      toast.success(`${sent ? 'Marked' : 'Un-marked'} as sent via ${channel === 'email' ? 'Email' : 'WhatsApp'}`);
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    }
  };


  // the invoice form's `fx_rate_inr` so the INR-equivalent is shown live.
  const [fxFetching, setFxFetching] = useState(false);
  const fetchFxRate = async () => {
    if (!form.currency || form.currency === 'INR') return;
    setFxFetching(true);
    try {
      const r = await axios.get(`${API_URL}/api/fx-rate?base=${form.currency}&target=INR`, { withCredentials: true });
      const rate = Number(r.data?.rate || 0);
      if (rate > 0) {
        setForm((f) => ({ ...f, fx_rate_inr: rate }));
        toast.success(`Live rate fetched: 1 ${form.currency} = ₹${rate.toFixed(4)} (${r.data?.source})`);
      } else {
        toast.error('Received empty rate from the FX provider');
      }
    } catch (e) {
      toast.error('FX fetch failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setFxFetching(false);
    }
  };

  // Build the full HTML string for an invoice (used by both print preview & PDF export).
  // includePrintScript = true → triggers window.print() on load (for the new-window flow).
  const buildInvoiceHtml = async (inv, includePrintScript = true) => {
    const client = clientMap[inv.client_id] || {};
    const company = getCo(inv.company_id);
    // Currency-aware formatter for the print template (scope-local override of `fmt`)
    const _ccy = CCY[inv.currency || 'INR'] || CCY.INR;
    const fmt = (n) => fmtCcy(n, inv.currency || 'INR');
    const logoUrl = company.logo_url
      ? (company.logo_url.startsWith('http') ? company.logo_url : `${API_URL}${company.logo_url}`)
      : 'https://customer-assets.emergentagent.com/job_6cece927-fe7d-443b-b094-47167d0b0812/artifacts/cfgq9z4h_logo-v8-black.png';
    // Force blue theme for all invoice prints (per Dextra brand guide)
    const accent = '#185FA5';
    const bank = company.bank || {};

    // Generate UPI QR (PNG data URL) if UPI ID present
    let qrDataUrl = '';
    if (bank.upi) {
      try {
        const QRLib = (await import('qrcode')).default;
        const upiLink = buildUpiLink(
          bank.upi,
          bank.beneficiary || company.name,
          inv.total,
          `INV ${inv.invoice_number}`,
        );
        qrDataUrl = await QRLib.toDataURL(upiLink, { margin: 1, width: 160 });
      } catch (e) { console.warn('QR generation failed', e); }
    }

    // GST/Tax breakdown for print:
    const isGstCompany = !!company.gst_enabled;
    const chips = (inv.invoice_taxes || []);
    const taxable = (inv.sub_total || 0) - (inv.discount_amount || 0);
    let taxRowsHtml = '';
    if (chips.length) {
      taxRowsHtml = chips.map((t) => {
        const amt = taxable * (Number(t.percentage) || 0) / 100;
        return `<tr><td class="lbl">${t.name} (${t.percentage}%)</td><td class="val">${fmt(amt)}</td></tr>`;
      }).join('');
    } else if (isGstCompany && (inv.tax_amount || 0) > 0) {
      const sameState = (client.state || '').trim().toLowerCase() === (company.state || '').trim().toLowerCase();
      const taxHalf = (inv.tax_amount || 0) / 2;
      const taxPctEff = (inv.items || []).find((it) => it.tax_percentage)?.tax_percentage || inv.tax_percentage || 0;
      const taxPctHalf = taxPctEff / 2;
      taxRowsHtml = sameState
        ? `<tr><td class="lbl">CGST (${taxPctHalf}%)</td><td class="val">${fmt(taxHalf)}</td></tr>
           <tr><td class="lbl">SGST (${taxPctHalf}%)</td><td class="val">${fmt(taxHalf)}</td></tr>`
        : `<tr><td class="lbl">IGST (${taxPctEff}%)</td><td class="val">${fmt(inv.tax_amount)}</td></tr>`;
    } else if (inv.tax_amount) {
      taxRowsHtml = `<tr><td class="lbl">Tax</td><td class="val">${fmt(inv.tax_amount)}</td></tr>`;
    }
    const isGst = isGstCompany;

    const itemRows = (inv.items || []).map((it, i) => `
      <tr>
        <td style="text-align:center">${i + 1}</td>
        <td>
          <div style="font-weight:600">${it.description || ''}</div>
          ${it.details ? `<div style="color:#374151;font-size:11.5px;line-height:1.5;margin-top:4px;white-space:pre-wrap;padding-left:2px">${String(it.details).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/\n/g,'<br/>')}</div>` : ''}
          ${it.hsn_sac && isGst ? `<div style="color:#6b7280;font-size:10px;margin-top:2px">HSN/SAC: ${it.hsn_sac}</div>` : ''}
        </td>
        <td style="text-align:right">${Number(it.qty).toFixed(2)}</td>
        <td style="text-align:right">${fmt(it.unit_price)}</td>
        <td style="text-align:right">${it.tax_percentage ? it.tax_percentage + '%' : '--'}</td>
        <td style="text-align:right">${fmt(it.amount || (it.qty * it.unit_price))}</td>
      </tr>`).join('');

    return `
<!DOCTYPE html><html><head><title>Invoice ${inv.invoice_number}</title>
<style>
  @page { size: A4; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family:'Helvetica Neue', Arial, sans-serif; color:#0f1729; font-size:12px; margin:0; position:relative; }

  /* Decorative blue triangle in top-right corner */
  .corner-deco { position:fixed; top:0; right:0; width:0; height:0;
    border-top: 90px solid ${accent}; border-left: 90px solid transparent; z-index:0; }
  .corner-deco-2 { position:fixed; top:0; right:0; width:0; height:0;
    border-top: 48px solid ${accent}cc; border-left: 48px solid transparent; z-index:1; }

  .top { display:flex; justify-content:space-between; align-items:flex-start; padding-bottom:18px; position:relative; z-index:2; }
  .brand { display:flex; align-items:center; gap:14px; }
  .brand img { height:88px; max-width:280px; object-fit:contain; }
  .brand-text { font-size:22px; font-weight:800; color:${accent}; letter-spacing:2px; font-family:'Helvetica Neue', Arial, sans-serif; }
  .brand-sub { font-size:10px; letter-spacing:4px; color:${accent}; opacity:.7; margin-top:2px; }

  .invoice-title { text-align:right; margin-right:110px; }
  .invoice-title h1 { margin:0; font-size:46px; font-weight:900; color:${accent}; letter-spacing:3px; line-height:1; }
  .invoice-title .meta { margin-top:14px; font-size:11px; color:#334155; }
  .invoice-title .meta div { margin:3px 0; }
  .invoice-title .meta strong { color:${accent}; display:inline-block; min-width:90px; }

  .company-meta { font-size:11px; color:#475569; margin-top:8px; line-height:1.5; }
  .company-meta strong { color:#0f1729; }

  .parties { display:grid; grid-template-columns:1fr 1fr; gap:30px; padding:18px 0; border-top:2px solid ${accent}; border-bottom:1px solid #e2e8f0; }
  .parties h4 { margin:0 0 6px 0; font-size:10px; color:${accent}; letter-spacing:1.5px; text-transform:uppercase; font-weight:700; }
  .parties .name { font-weight:700; font-size:15px; margin-bottom:4px; color:#0f1729; }
  .parties .line { color:#475569; line-height:1.5; white-space:pre-line; font-size:11px; }

  table.items { width:100%; border-collapse:collapse; margin-top:18px; }
  table.items th { background:${accent}; color:#fff; padding:11px 10px; font-size:11px; text-align:left; text-transform:uppercase; letter-spacing:0.5px; font-weight:700; }
  table.items th.r, table.items td.r { text-align:right; }
  table.items th.c, table.items td.c { text-align:center; }
  table.items td { padding:10px; border-bottom:1px solid #e2e8f0; vertical-align:top; }
  table.items tbody tr:nth-child(even) td { background:#f8fafc; }

  .summary-wrap { display:grid; grid-template-columns:1fr 320px; gap:24px; margin-top:18px; align-items:flex-start; }
  .inwords { padding:10px 12px; font-size:11px; background:${accent}10; border-left:3px solid ${accent}; color:#0f1729; border-radius:0 4px 4px 0; }
  .inwords strong { color:${accent}; }

  .totals { border:1px solid #e2e8f0; border-radius:6px; overflow:hidden; }
  .totals table { width:100%; border-collapse:collapse; }
  .totals td { padding:8px 14px; font-size:12px; }
  .totals .lbl { color:#475569; text-align:left; }
  .totals .val { text-align:right; font-weight:600; color:#0f1729; }
  .totals tr.subline td { border-bottom:1px solid #e2e8f0; }
  .totals tr.grand td { background:${accent}; color:#fff; font-size:14px; font-weight:800; padding:14px; text-transform:uppercase; letter-spacing:1px; }
  .totals tr.grand .lbl { color:#fff; }
  .totals tr.grand .val { color:#fff; font-size:18px; }

  .terms { margin-top:24px; padding-top:14px; border-top:1px solid #e2e8f0; font-size:11px; color:#475569; line-height:1.6; }
  .terms h4 { margin:0 0 8px 0; font-size:11px; color:${accent}; letter-spacing:1.5px; text-transform:uppercase; font-weight:700; }
  .bank-grid { display:grid; grid-template-columns:1fr 150px; gap:24px; align-items:flex-start; }
  .bank-grid .qr { text-align:center; }
  .bank-grid .qr img { width:140px; height:140px; border:1px solid #e2e8f0; padding:4px; background:#fff; }
  .bank-grid .qr .cap { font-size:9px; margin-top:6px; color:${accent}; font-weight:600; letter-spacing:0.5px; text-transform:uppercase; }

  .foot { margin-top:24px; display:flex; justify-content:space-between; align-items:flex-end; font-size:10px; color:#94a3b8; padding-top:14px; border-top:1px solid #e2e8f0; }
  .sign { text-align:right; padding-top:30px; min-width:230px; font-size:11px; color:#0f1729; }
  .sign .lbl { font-weight:700; margin-bottom:36px; color:${accent}; text-transform:uppercase; letter-spacing:1px; font-size:11px; }
  .sign .ln { border-top:1px solid #64748b; padding-top:5px; font-weight:600; }

  @media print {
    .corner-deco, .corner-deco-2 { position:absolute; }
  }
</style></head>
<body>
  <div class="corner-deco"></div>
  <div class="corner-deco-2"></div>

  <div class="top">
    <div>
      <div class="brand">
        <img src="${logoUrl}" />
      </div>
      <div class="company-meta">
        <strong style="font-size:13px;color:${accent}">${company.name}</strong><br/>
        <span style="white-space:pre-line">${company.address || ''}</span><br/>
        ${company.phone || ''} ${company.phone && company.email ? '·' : ''} ${company.email || ''}
        ${company.gstin ? `<br/><strong>GSTIN:</strong> ${company.gstin}` : ''}
      </div>
    </div>
    <div class="invoice-title">
      <h1>INVOICE</h1>
      <div class="meta">
        <div><strong>Invoice ID:</strong> #${inv.invoice_number || '-'}</div>
        <div><strong>Invoice Date:</strong> ${toDDMMYYYY(inv.invoice_date)}</div>
        <div><strong>Due Date:</strong> ${toDDMMYYYY(inv.due_date)}</div>
        ${isGst && inv.place_of_supply ? `<div><strong>Place of Supply:</strong> ${inv.place_of_supply}</div>` : ''}
        <div><strong>Status:</strong> ${(inv.status || 'unpaid').toUpperCase()}</div>
      </div>
    </div>
  </div>

  <div class="parties">
    <div>
      <h4>To</h4>
      <div class="name">${((client.first_name || '') + ' ' + (client.last_name || '')).trim() || '—'}</div>
      ${client.company_name ? `<div class="line"><strong>${client.company_name}</strong></div>` : ''}
      ${client.address ? `<div class="line">${client.address}</div>` : ''}
      ${client.city || client.state ? `<div class="line">${[client.city, client.state, client.postal_code].filter(Boolean).join(', ')}</div>` : ''}
      ${client.gstin ? `<div class="line"><strong>GSTIN:</strong> ${client.gstin}</div>` : ''}
    </div>
    <div>
      <h4>From</h4>
      <div class="name">${company.name}</div>
      ${company.address ? `<div class="line">${company.address}</div>` : ''}
      ${company.phone ? `<div class="line">${company.phone}</div>` : ''}
      ${company.email ? `<div class="line">${company.email}</div>` : ''}
      ${company.gstin ? `<div class="line"><strong>GSTIN:</strong> ${company.gstin}</div>` : ''}
      <div style="margin-top:8px"><h4>Payment Mode</h4><div class="line">${inv.payment_mode || 'NEFT / UPI'}</div></div>
    </div>
  </div>

  <table class="items">
    <thead><tr>
      <th class="c" style="width:32px">#</th>
      <th>Details</th>
      <th class="r" style="width:60px">Qty</th>
      <th class="r" style="width:110px">Unit Price</th>
      <th class="r" style="width:70px">Tax(%)</th>
      <th class="r" style="width:130px">Amount</th>
    </tr></thead>
    <tbody>${itemRows}</tbody>
  </table>

  <div class="summary-wrap">
    <div>
      <div class="inwords"><strong>In Words:</strong> ${amountInWords(inv.total, inv.currency || 'INR')}</div>
      ${inv.notes ? `<div style="margin-top:14px;font-size:11px;color:#475569"><strong style="color:${accent};text-transform:uppercase;letter-spacing:1px;font-size:10px">Notes</strong><br/>${inv.notes}</div>` : ''}
    </div>
    <div class="totals">
      <table>
        <tr class="subline"><td class="lbl">Sub Total</td><td class="val">${fmt(inv.sub_total)}</td></tr>
        ${inv.discount_amount ? `<tr class="subline"><td class="lbl">Discount</td><td class="val">- ${fmt(inv.discount_amount)}</td></tr>` : ''}
        ${taxRowsHtml.replace(/<tr>/g, '<tr class="subline">')}
        <tr class="subline"><td class="lbl"><strong>Total</strong></td><td class="val"><strong>${fmt(inv.total)}</strong></td></tr>
        ${(inv.paid_amount || 0) > 0 ? `<tr class="subline"><td class="lbl">Paid</td><td class="val" style="color:#16a34a">- ${fmt(inv.paid_amount)}</td></tr>` : ''}
        <tr class="grand"><td class="lbl">Total Due</td><td class="val">${fmt(inv.total - (inv.paid_amount || 0))}</td></tr>
      </table>
    </div>
  </div>

  <div class="terms">
    <h4>Bank Details &amp; Payment Terms</h4>
    <div class="bank-grid">
      <div>
        Payment may be transferred by NEFT / IMPS / Online transfer to:<br/>
        <strong>Account Name:</strong> ${bank.beneficiary || ''}<br/>
        <strong>Account Number:</strong> ${bank.account || ''}<br/>
        <strong>Bank Name:</strong> ${bank.bank_name || ''}${bank.bank_address ? ' · ' + bank.bank_address : ''}<br/>
        <strong>IFSC Code:</strong> ${bank.ifsc || ''}${bank.swift ? ' · <strong>SWIFT:</strong> ' + bank.swift : ''}<br/>
        ${bank.upi ? `<strong>UPI ID:</strong> ${bank.upi}` : ''}
        ${inv.terms ? `<br/><br/>${inv.terms}` : ''}
      </div>
      ${qrDataUrl ? `
        <div class="qr">
          <img src="${qrDataUrl}" />
          <div class="cap">Scan to pay via UPI</div>
        </div>
      ` : ''}
    </div>
  </div>

  <div class="foot">
    <div>This is a system-generated invoice.${company.email ? ` For queries email ${company.email}` : ''}</div>
    <div class="sign">
      <div class="lbl">${company.signature_label || 'For ' + company.name}</div>
      <div class="ln">${company.signatory || 'Authorised Signatory'}</div>
    </div>
  </div>
  ${includePrintScript ? '<script>window.onload=function(){ window.print(); };</script>' : ''}
</body></html>`;
  };

  const printInvoice = async (inv) => {
    const html = await buildInvoiceHtml(inv, true);
    const w = window.open('', '', 'width=900,height=700');
    w.document.write(html);
    w.document.close();
  };

  // Build a clean filename per user spec:  "<Client Company Name> INV <number>.pdf"
  // - Strips DXT/DXC (company-short) prefix from invoice number
  // - Keeps spaces in client name + uses spaces (not underscores) around INV
  // - Sanitises any unsafe filesystem chars
  const pdfFilenameFor = (inv) => {
    const c = clientMap[inv.client_id] || {};
    const rawClient =
      (c.company_name && c.company_name.trim()) ||
      `${c.first_name || ''} ${c.last_name || ''}`.trim() ||
      'Client';
    const safeClient = rawClient
      .replace(/[\\/:*?"<>|]+/g, '')   // strip filesystem-illegal chars only
      .replace(/\s+/g, ' ')            // collapse whitespace
      .trim()
      .slice(0, 80);
    const num = String(inv.invoice_number || inv.id || '')
      // Strip a leading 2-5 letter company short (DXT-, DXC-, etc.) + optional dash/underscore/space
      .replace(/^[A-Za-z]{2,5}[-_ ]?/, '')
      .replace(/[\\/:*?"<>|]+/g, '')
      .trim();
    return `${safeClient} INV ${num || 'NA'}.pdf`;
  };

  // Server-side PDF rendering (WeasyPrint) — produces a real vector PDF
  // matching the browser print template exactly. This replaces the previous
  // html2pdf / html2canvas raster approach which collapsed table layout.
  const downloadInvoicePdf = async (inv) => {
    const html = await buildInvoiceHtml(inv, false);
    const filename = pdfFilenameFor(inv);
    const resp = await axios.post(
      `${API_URL}/api/render-pdf`,
      { html, filename },
      { withCredentials: true, responseType: 'blob' },
    );
    const blob = new Blob([resp.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Defer revoke so the browser can start the download first
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const downloadSelectedPdfs = async (invoicesToExport) => {
    if (!invoicesToExport.length) return;
    setBulkBusy(true);
    setBulkProgress({ done: 0, total: invoicesToExport.length });
    try {
      for (let i = 0; i < invoicesToExport.length; i += 1) {
        // Sequentially to avoid spawning many parallel canvases
        // eslint-disable-next-line no-await-in-loop
        await downloadInvoicePdf(invoicesToExport[i]);
        setBulkProgress({ done: i + 1, total: invoicesToExport.length });
        // small pause so browser can flush each download before the next
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 350));
      }
    } catch (e) {
      console.error('Bulk PDF export failed:', e);
      alert('Bulk PDF export failed: ' + (e?.message || 'unknown error'));
    } finally {
      setBulkBusy(false);
      setBulkProgress({ done: 0, total: 0 });
    }
  };

  return (
    <div data-testid="invoices-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--dx-text)' }}>Invoices</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>Raise & track GST invoices</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(emptyInv()); setEdit(false); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-invoice-btn" className="text-white" style={{ background: 'var(--dx-primary)' }}>
              <Plus size={18} className="mr-1" /> New Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{edit ? 'Edit Invoice' : 'New Invoice'}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label>Bill From (Company) *</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                    <SelectTrigger data-testid="inv-company"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {companies.map((co) => (
                        <SelectItem key={co.id} value={co.id}>{co.name}{co.gst_enabled ? ' (GST)' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Client *</Label>
                  <div className="flex gap-1 mb-1.5">
                    {[
                      { k: 'all', label: 'All' },
                      { k: 'gst', label: 'GST Clients' },
                      { k: 'nongst', label: 'Non-GST' },
                    ].map((f) => (
                      <button
                        key={f.k}
                        type="button"
                        onClick={() => setClientGstFilter(f.k)}
                        className={`text-[10px] px-2 py-1 rounded border transition ${clientGstFilter === f.k ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-semibold' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}
                        data-testid={`inv-client-filter-${f.k}`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <SearchableSelect
                    testId="inv-client"
                    value={form.client_id}
                    onChange={(v) => setForm({ ...form, client_id: v })}
                    placeholder="Select client (type to search)"
                    options={clients
                      .filter((c) => {
                        const hasGst = !!(c.gstin || '').trim();
                        if (clientGstFilter === 'gst' && !hasGst) return false;
                        if (clientGstFilter === 'nongst' && hasGst) return false;
                        return true;
                      })
                      .map((c) => {
                      const fullName = `${c.first_name || ''} ${c.last_name || ''}`.trim();
                      return {
                        value: c.id,
                        label: fullName || c.company_name || '(unnamed)',
                        sublabel: c.company_name && fullName ? c.company_name : (c.email || ''),
                        search: `${fullName} ${c.company_name || ''} ${c.email || ''} ${c.phone || ''} ${c.gstin || ''}`,
                      };
                    })}
                  />
                </div>
                {/* Fixed-scope Project link — auto-filters to picked client */}
                <div className="col-span-2">
                  <Label>Link to Project <span className="text-xs font-normal text-gray-500">(optional — for fixed-scope budget tracking)</span></Label>
                  <SearchableSelect
                    testId="inv-project"
                    value={form.project_id || ''}
                    onChange={(v) => setForm({ ...form, project_id: v })}
                    placeholder="Pick a project (type to search)…"
                    options={[
                      { value: '', label: '— None (not linked) —' },
                      ...projects
                        .filter((p) => !p.is_recurring && (!form.client_id || p.client_id === form.client_id))
                        .map((p) => ({
                          value: p.id,
                          label: `${p.name}${p.code ? ` [${p.code}]` : ''}`,
                          sublabel: p.contract_value > 0
                            ? `${p.contract_currency || 'INR'} ${Math.round(p.contract_value).toLocaleString('en-IN')}`
                            : '',
                          search: `${p.name} ${p.code || ''}`,
                        })),
                    ]}
                  />
                  {projectBilling && (() => {
                    const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n));
                    // Compute this invoice's total in INR for the check
                    const rate = parseFloat(form.fx_rate_inr) || 1.0;
                    const currentTotalInr = (parseFloat(form.total) || 0) * rate;
                    const remainingAfter = projectBilling.contract_value_inr - projectBilling.invoiced_inr - currentTotalInr;
                    const exceeds = remainingAfter < 0 && projectBilling.contract_value_inr > 0;
                    return (
                      <div className={`mt-2 p-2 rounded border text-xs ${exceeds ? 'bg-rose-50 border-rose-300' : 'bg-blue-50 border-blue-200'}`} data-testid="project-budget-banner">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">
                            {exceeds ? '⚠️' : '💰'} {projectBilling.contract_currency} {fmt(projectBilling.contract_value)} budget
                          </span>
                          <span className={`font-bold ${exceeds ? 'text-rose-700' : 'text-emerald-700'}`}>
                            {projectBilling.pct}% used
                          </span>
                        </div>
                        <div className="mt-1">
                          <span className="text-blue-700">Invoiced: <strong>₹ {fmt(projectBilling.invoiced_inr)}</strong> ({projectBilling.invoice_count} inv)</span> ·
                          <span className="text-emerald-700 ml-1">Received: <strong>✓ ₹ {fmt(projectBilling.paid_inr)}</strong></span> ·
                          <span className="text-rose-700 ml-1">Due: <strong>₹ {fmt(Math.max(0, projectBilling.invoiced_inr - projectBilling.paid_inr))}</strong></span>
                        </div>
                        <div className="mt-0.5">
                          <span className={`${remainingAfter < 0 ? 'text-rose-700 font-semibold' : 'text-purple-700'}`}>
                            Yet to invoice: <strong>₹ {fmt(Math.max(0, projectBilling.contract_value_inr - projectBilling.invoiced_inr))}</strong>
                          </span>
                        </div>
                        {exceeds && (
                          <div className="mt-1 text-rose-700 font-semibold">
                            This invoice ({form.currency} {fmt(form.total || 0)}) exceeds available budget by ₹ {fmt(Math.abs(remainingAfter))}. Saving is still allowed — verify scope-creep was approved.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <Label>Invoice No.</Label>
                  <Input value={form.invoice_number}
                    placeholder={`${getCo(form.company_id).short || 'INV'}-000001 (auto if blank)`}
                    onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="inv-no" />
                </div>
                <div>
                  <Label>Invoice Date *</Label>
                  <Input required type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="inv-date" />
                </div>
                <div>
                  <Label>Due Date *</Label>
                  <Input required type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="inv-due" />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['draft', 'unpaid', 'partial', 'paid', 'overdue'].map((s) => (<SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Currency</Label>
                  <Select value={form.currency || 'INR'} onValueChange={(v) => setForm({ ...form, currency: v, fx_rate_inr: v === 'INR' ? 1 : (form.fx_rate_inr || 1) })}>
                    <SelectTrigger data-testid="inv-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.flag} {c.code} — {c.symbol} {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.currency && form.currency !== 'INR' && (
                    <div className="mt-2 p-2.5 rounded-md border border-indigo-200 bg-indigo-50/50">
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-indigo-800">FX rate → INR</Label>
                        <button type="button" onClick={fetchFxRate} disabled={fxFetching}
                          className="text-[11px] font-semibold flex items-center gap-1 px-2 py-1 rounded bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
                          data-testid="fetch-fx-rate-btn">
                          <ArrowsClockwise size={11} weight="bold" className={fxFetching ? 'animate-spin' : ''} />
                          {fxFetching ? 'Fetching…' : 'Fetch live rate'}
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-600">1 {form.currency} =</span>
                        <Input
                          type="number"
                          step="0.0001"
                          value={form.fx_rate_inr || ''}
                          onChange={(e) => setForm({ ...form, fx_rate_inr: parseFloat(e.target.value) || 0 })}
                          className="h-8 text-sm max-w-[120px]"
                          placeholder="0.00"
                          data-testid="inv-fx-rate"
                        />
                        <span className="text-xs text-gray-600">INR</span>
                      </div>
                      {form.fx_rate_inr > 0 && totals.total > 0 && (
                        <div className="text-[11px] text-indigo-800 mt-1.5">
                          Invoice total ≈ <span className="font-bold">₹{(totals.total * Number(form.fx_rate_inr || 1)).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Payment Mode</Label>
                  <Input value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} placeholder="NEFT / UPI / Cash" />
                </div>
                {getCo(form.company_id).gst_enabled && (
                  <div>
                    <Label>Place of Supply</Label>
                    <Input value={form.place_of_supply} onChange={(e) => setForm({ ...form, place_of_supply: e.target.value })} placeholder="Tamil Nadu" />
                  </div>
                )}
                {form.status === 'partial' && (
                  <div>
                    <Label>Paid Amount</Label>
                    <Input type="number" step="0.01" value={form.paid_amount} onChange={(e) => setForm({ ...form, paid_amount: e.target.value })} />
                  </div>
                )}
              </div>

              {/* Line items */}
              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-sm">Line Items</h3>
                  <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="add-line-item">
                    <Plus size={14} className="mr-1" /> Add row
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr style={{ background: 'var(--dx-bg)' }}>
                      <th className="text-left p-2 text-xs uppercase">Description</th>
                      {getCo(form.company_id).gst_enabled && (
                        <th className="text-left p-2 text-xs uppercase w-28">HSN/SAC</th>
                      )}
                      <th className="text-right p-2 text-xs uppercase w-20">Qty</th>
                      <th className="text-right p-2 text-xs uppercase w-32">Unit Price</th>
                      <th className="text-right p-2 text-xs uppercase w-28">Tax %</th>
                      <th className="text-right p-2 text-xs uppercase w-32">Amount</th>
                      <th className="w-10"></th>
                    </tr></thead>
                    <tbody>
                      {form.items.map((it, i) => {
                        const amt = (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0);
                        return (
                          <tr key={i} className="border-b">
                            <td className="p-1 align-top" style={{ minWidth: 320 }}>
                              <div className="space-y-1.5">
                                {/* Product dropdown — picks heading + price from masters */}
                                <Select
                                  value={products.find((pp) => pp.name === it.description)?.id || '__free'}
                                  onValueChange={(v) => {
                                    if (v === '__free') return;
                                    const p = products.find((pp) => pp.id === v);
                                    if (!p) return;
                                    setForm({
                                      ...form,
                                      items: form.items.map((x, idx) => idx === i ? {
                                        ...x,
                                        description: p.name,
                                        unit_price: p.unit_price ?? x.unit_price,
                                        hsn_sac: p.hsn_sac || x.hsn_sac,
                                      } : x),
                                    });
                                  }}>
                                  <SelectTrigger className="h-8 text-xs" data-testid={`item-product-select-${i}`}>
                                    <SelectValue placeholder="📦 Pick a product (optional)" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__free">— Free text (no product) —</SelectItem>
                                    {products.filter((p) => !!p.name).map((p) => (
                                      <SelectItem key={p.id} value={p.id}>
                                        {p.name}{p.unit_price ? ` — ₹${p.unit_price}` : ''}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {/* Heading — bold one-liner */}
                                <div>
                                  <label className="text-[9px] uppercase tracking-wider font-bold text-gray-500 block mb-0.5">Heading / Description *</label>
                                  <Input
                                    className="h-9 font-semibold"
                                    placeholder="e.g. Website Maintenance"
                                    value={it.description}
                                    onChange={(e) => setItem(i, 'description', e.target.value)}
                                    data-testid={`item-desc-${i}`}
                                  />
                                </div>
                                {/* Details — multi-line, printed under the heading in the invoice PDF */}
                                <div>
                                  <label className="text-[9px] uppercase tracking-wider font-bold text-gray-500 block mb-0.5">
                                    Detailed Description <span className="normal-case text-gray-400 font-normal">(shows under heading on the PDF)</span>
                                  </label>
                                  <textarea
                                    className="w-full border rounded px-2 py-1.5 text-xs leading-snug min-h-[60px] resize-y bg-white"
                                    placeholder={"Line-by-line details, deliverables, notes\u2026\ne.g.\n- Monthly maintenance\n- Bug fixes and updates\n- 24×7 support"}
                                    value={it.details || ''}
                                    onChange={(e) => setItem(i, 'details', e.target.value)}
                                    data-testid={`item-details-${i}`}
                                  />
                                </div>
                              </div>
                            </td>
                            {getCo(form.company_id).gst_enabled && (
                              <td className="p-1"><Input className="h-9" value={it.hsn_sac} onChange={(e) => setItem(i, 'hsn_sac', e.target.value)} placeholder="HSN/SAC" data-testid={`item-hsn-${i}`} /></td>
                            )}
                            <td className="p-1"><Input className="h-9 text-right" type="number" step="0.01" value={it.qty}
                              onChange={(e) => setItem(i, 'qty', e.target.value)} data-testid={`item-qty-${i}`} /></td>
                            <td className="p-1"><Input className="h-9 text-right" type="number" step="0.01" value={it.unit_price}
                              onChange={(e) => setItem(i, 'unit_price', e.target.value)} data-testid={`item-price-${i}`} /></td>
                            <td className="p-1">
                              <Select value={String(it.tax_percentage)} onValueChange={(v) => setItem(i, 'tax_percentage', parseFloat(v))}>
                                <SelectTrigger className="h-9"><SelectValue placeholder="0" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="0">No tax</SelectItem>
                                  {taxes.map((t) => (<SelectItem key={t.id} value={String(t.percentage)}>{t.name}</SelectItem>))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-2 text-right font-semibold">{fmtCcy(amt, form.currency)}</td>
                            <td className="p-1 text-center">
                              <Button type="button" size="sm" variant="outline" className="text-red-600" onClick={() => delItem(i)}>
                                <Trash size={14} />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Discount + Totals preview */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Discount</Label>
                  <div className="flex gap-2">
                    <Input type="number" step="0.01" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="w-32" />
                    <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                      <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="amount">Fixed (₹)</SelectItem>
                        <SelectItem value="percentage">Percentage (%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Invoice-level Tax chips */}
                  <Label className="mt-3 block">Tax</Label>
                  <div
                    className="border rounded-md p-2 min-h-[44px] flex flex-wrap items-center gap-2"
                    data-testid="invoice-tax-chip-container"
                  >
                    {(form.invoice_taxes || []).length === 0 && (
                      <span className="text-xs" style={{ color: 'var(--dx-text3)' }}>
                        No taxes added
                      </span>
                    )}
                    {(form.invoice_taxes || []).map((t, ti) => (
                      <span
                        key={`${t.id || t.name}-${ti}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs border bg-white"
                        data-testid={`tax-chip-${t.name}`}
                      >
                        <button
                          type="button"
                          className="text-red-500 hover:text-red-700 font-bold"
                          onClick={() => removeInvoiceTax(t.id)}
                          aria-label={`remove ${t.name}`}
                        >×</button>
                        <span className="font-medium">{t.name}</span>
                      </span>
                    ))}
                  </div>
                  <Select
                    key={`tax-add-${(form.invoice_taxes || []).length}`}
                    onValueChange={(v) => { if (v) addInvoiceTax(v); }}
                  >
                    <SelectTrigger className="mt-1" data-testid="add-tax-select">
                      <SelectValue placeholder="+ Select Tax to add (CGST, SGST, IGST...)" />
                    </SelectTrigger>
                    <SelectContent>
                      {taxes
                        .filter((t) => !(form.invoice_taxes || []).some((x) => x.id === t.id))
                        .map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.name} ({t.percentage}%)</SelectItem>
                        ))}
                      {taxes.filter((t) => !(form.invoice_taxes || []).some((x) => x.id === t.id)).length === 0 && (
                        <SelectItem value="__none" disabled>No more taxes available</SelectItem>
                      )}
                    </SelectContent>
                  </Select>

                  <Label className="mt-3 block">Notes</Label>
                  <textarea className="w-full border rounded-md px-3 py-2 text-sm" value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
                <div className="p-4 rounded-md text-sm" style={{ background: 'var(--dx-bg)' }}>
                  <div className="flex justify-between py-1"><span>Sub Total</span><span className="font-semibold">{fmtCcy(totals.sub, form.currency)}</span></div>
                  <div className="flex justify-between py-1"><span>Discount</span><span>- {fmtCcy(totals.disc, form.currency)}</span></div>
                  {(form.invoice_taxes || []).map((t, ti) => {
                    const taxable = totals.sub - totals.disc;
                    const amt = taxable * (parseFloat(t.percentage) || 0) / 100;
                    return (
                      <div key={`${t.id || t.name}-${ti}`} className="flex justify-between py-1 text-xs" style={{ color: 'var(--dx-text2)' }}>
                        <span>{t.name} ({t.percentage}%)</span><span>+ {fmtCcy(amt, form.currency)}</span>
                      </div>
                    );
                  })}
                  <div className="flex justify-between py-1"><span>Tax (Total)</span><span>+ {fmtCcy(totals.tax, form.currency)}</span></div>
                  <div className="flex justify-between py-2 mt-1 border-t font-bold text-base" style={{ color: 'var(--dx-primary)' }}>
                    <span>Total</span><span data-testid="totals-total">{fmtCcy(totals.total, form.currency)}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" className="text-white" style={{ background: 'var(--dx-primary)' }} data-testid="save-invoice-btn">
                  {edit ? 'Update Invoice' : 'Create Invoice'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="dx-card">
        {/* Company tabs — quick filter by billing company */}
        {companies.length > 1 && (
          <div className="px-3 pt-3 flex items-center gap-1.5 flex-wrap" data-testid="invoice-company-tabs">
            <button
              type="button"
              onClick={() => setCompanyTab('all')}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${companyTab === 'all' ? 'text-white border-transparent' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              style={companyTab === 'all' ? { background: 'var(--dx-primary)' } : undefined}
              data-testid="invoice-company-tab-all"
            >
              All Companies
              <span className="ml-1.5 text-[10px] font-bold opacity-80">{list.length}</span>
            </button>
            {companies.map((co) => {
              const cnt = list.filter((i) => i.company_id === co.id).length;
              const active = companyTab === co.id;
              return (
                <button
                  key={co.id}
                  type="button"
                  onClick={() => setCompanyTab(co.id)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition flex items-center gap-1.5 ${active ? 'text-white border-transparent' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  style={active ? { background: co.accent || 'var(--dx-primary)' } : { borderColor: (co.accent || '#d1d5db') + '55' }}
                  data-testid={`invoice-company-tab-${co.id}`}
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ background: co.accent || '#94a3b8' }}
                  />
                  {co.short || co.name}
                  {co.gst_enabled && <span className="text-[9px] opacity-80">GST</span>}
                  <span className="ml-1 text-[10px] font-bold opacity-80">{cnt}</span>
                </button>
              );
            })}
          </div>
        )}
        <div className="p-3 flex items-center gap-3 flex-wrap border-b" style={{ borderColor: 'var(--dx-border)' }}>
          <input
            type="text"
            placeholder="Search by invoice #, client, company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="text-sm pl-3 pr-3 py-2 border rounded outline-none focus:ring-2 focus:ring-emerald-500/30 flex-1 min-w-[220px]"
            data-testid="invoice-search"
          />
          {/* Multi-select status filter — checkboxes so admin can combine e.g. Unpaid + Partial + Overdue */}
          {(() => {
            const STATUS_OPTS = [
              { v: 'draft',   label: 'Draft',   color: 'bg-gray-100 text-gray-700 border-gray-300' },
              { v: 'unpaid',  label: 'Unpaid',  color: 'bg-amber-50 text-amber-800 border-amber-300' },
              { v: 'partial', label: 'Partial', color: 'bg-blue-50 text-blue-800 border-blue-300' },
              { v: 'paid',    label: 'Paid',    color: 'bg-emerald-50 text-emerald-800 border-emerald-300' },
              { v: 'overdue', label: 'Overdue', color: 'bg-rose-50 text-rose-800 border-rose-300' },
              { v: 'written_off', label: 'Written Off', color: 'bg-slate-200 text-slate-700 border-slate-400' },
            ];
            const toggle = (v) => setStatusFilter((cur) => cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
            return (
              <div className="flex items-center gap-1 flex-wrap" data-testid="invoice-status-filter">
                <button
                  type="button"
                  onClick={() => setStatusFilter([])}
                  className={`text-xs px-2 py-1.5 rounded border font-semibold transition-all ${statusFilter.length === 0 ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'}`}
                  data-testid="status-filter-all"
                >All</button>
                {STATUS_OPTS.map((o) => {
                  const on = statusFilter.includes(o.v);
                  return (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => toggle(o.v)}
                      className={`text-xs px-2 py-1.5 rounded border font-semibold transition-all ${on ? o.color + ' ring-1 ring-offset-1 ring-current' : 'bg-white text-gray-500 border-gray-300 hover:border-gray-400'}`}
                      data-testid={`status-filter-${o.v}`}
                    >
                      {on && '✓ '}{o.label}
                    </button>
                  );
                })}
              </div>
            );
          })()}
          <select
            value={currencyFilter}
            onChange={(e) => setCurrencyFilter(e.target.value)}
            className="text-sm px-2 py-2 border rounded bg-white"
            data-testid="invoice-currency-filter"
          >
            <option value="all">All Currencies</option>
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
            ))}
          </select>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 font-medium">From</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="text-sm px-2 py-2 border rounded bg-white"
              data-testid="invoice-date-from"
            />
            <span className="text-xs text-gray-500 font-medium">To</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="text-sm px-2 py-2 border rounded bg-white"
              data-testid="invoice-date-to"
            />
            {(dateFrom || dateTo) && (
              <button
                type="button"
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                className="text-xs px-2 py-1 rounded border bg-white hover:bg-gray-50"
                data-testid="invoice-date-clear"
              >Clear</button>
            )}
          </div>
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div
            className="px-3 py-2 flex items-center gap-3 border-b bg-blue-50"
            style={{ borderColor: 'var(--dx-border)' }}
            data-testid="invoice-bulk-bar"
          >
            <span className="text-sm font-semibold text-blue-900" data-testid="invoice-selected-count">
              {selectedIds.size} selected
            </span>
            <Button
              size="sm"
              type="button"
              disabled={bulkBusy}
              onClick={() => {
                const toExport = filteredInvoices.filter((inv) => selectedIds.has(inv.id));
                downloadSelectedPdfs(toExport);
              }}
              className="text-white"
              style={{ background: 'var(--dx-primary)' }}
              data-testid="invoice-bulk-download-pdf"
            >
              <Download size={14} className="mr-1.5" />
              {bulkBusy
                ? `Downloading ${bulkProgress.done}/${bulkProgress.total}…`
                : `Download ${selectedIds.size} PDF${selectedIds.size > 1 ? 's' : ''}`}
            </Button>
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs px-2 py-1 rounded border bg-white hover:bg-gray-50"
              data-testid="invoice-bulk-clear"
            >Clear selection</button>
          </div>
        )}

        <Table>
          <TableHeader><TableRow style={{ background: 'var(--dx-bg)' }}>
            <TableHead style={{ width: 36 }}>
              <input
                type="checkbox"
                checked={pagedInvoices.length > 0 && pagedInvoices.every((inv) => selectedIds.has(inv.id))}
                onChange={toggleAll}
                aria-label="Select all invoices on this page"
                data-testid="invoice-select-all"
              />
            </TableHead>
            <TableHead>#</TableHead>
            <TableHead>INVOICE #</TableHead><TableHead>COMPANY</TableHead><TableHead>CLIENT</TableHead>
            <TableHead>INVOICE DATE</TableHead><TableHead>DUE DATE</TableHead>
            <TableHead className="text-right">AMOUNT</TableHead>
            <TableHead className="text-right">OUTSTANDING</TableHead>
            <TableHead>STATUS</TableHead>
            <TableHead className="text-center">SENT</TableHead>
            <TableHead className="text-right">ACTIONS</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(() => {
              const paged = pagedInvoices;
              if (paged.length === 0) {
                return (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8" style={{ color: 'var(--dx-text2)' }}>
                      <FileText size={32} className="mx-auto mb-2" style={{ color: 'var(--dx-text3)' }} />
                      {list.length === 0 ? 'No invoices yet. Click "New Invoice" to create one.' : 'No invoices match your filters.'}
                    </TableCell>
                  </TableRow>
                );
              }
              return paged.map((inv, idx) => {
                const c = clientMap[inv.client_id];
                const co = getCo(inv.company_id);
                const col = STATUS_COLOR[inv.status] || STATUS_COLOR.unpaid;
                const ccy = inv.currency || 'INR';
                const ccyMeta = CCY[ccy] || CCY.INR;
                const isSel = selectedIds.has(inv.id);
                const serial = startIdx + idx + 1;
              return (
                <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`} style={isSel ? { background: '#eff6ff' } : undefined}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggleOne(inv.id)}
                      aria-label={`Select invoice ${inv.invoice_number}`}
                      data-testid={`invoice-select-${inv.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-gray-400">{serial}</TableCell>
                  <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                  <TableCell>
                    <span className="px-2 py-1 rounded text-[10px] font-bold uppercase"
                      style={{ background: co.accent + '22', color: co.accent }}>
                      {co.short}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div>{c ? `${c.first_name} ${c.last_name || ''}` : '—'}</div>
                    {c?.company_name && <div className="text-xs" style={{ color: 'var(--dx-text3)' }}>{c.company_name}</div>}
                  </TableCell>
                  <TableCell>{toDDMMYYYY(inv.invoice_date)}</TableCell>
                  <TableCell>{toDDMMYYYY(inv.due_date)}</TableCell>
                  <TableCell className="text-right font-semibold">
                    <div className="flex items-center justify-end gap-1.5">
                      {ccy !== 'INR' && <span className="text-[9px] px-1 py-0.5 rounded bg-indigo-50 text-indigo-700 font-bold">{ccyMeta.flag} {ccy}</span>}
                      <span>{fmtCcy(inv.total, ccy)}</span>
                    </div>
                    {ccy !== 'INR' && Number(inv.fx_rate_inr || 0) > 0 && (
                      <div className="text-[10px] text-gray-500 font-normal">≈ ₹{Number((inv.total || 0) * Number(inv.fx_rate_inr || 1)).toLocaleString('en-IN', { maximumFractionDigits: 0 })} @ {Number(inv.fx_rate_inr).toFixed(2)}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {(() => {
                      const balance = Math.max(0, Number(inv.total || 0) - Number(inv.paid_amount || 0));
                      const isZero = balance <= 0.001;
                      const inrBalance = balance * Number(inv.fx_rate_inr || 1);
                      return (
                        <>
                          <div className={isZero ? 'text-emerald-600' : 'text-rose-600'}>
                            {isZero ? '0' : fmtCcy(balance, ccy)}
                          </div>
                          {!isZero && ccy !== 'INR' && Number(inv.fx_rate_inr || 0) > 0 && (
                            <div className="text-[10px] text-gray-500 font-normal">≈ ₹{inrBalance.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
                          )}
                        </>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase"
                      style={{ background: col.bg, color: col.color }}>{inv.status}</span>
                  </TableCell>
                  {/* SENT — two toggle chips (Email + WhatsApp). Green when marked sent, gray otherwise. */}
                  <TableCell className="text-center">
                    <div className="flex justify-center gap-1">
                      <button
                        type="button"
                        onClick={() => markSent(inv, 'email', !inv.sent_via_email)}
                        title={inv.sent_via_email ? `Emailed on ${(inv.email_sent_at || '').slice(0, 10)} — click to un-mark` : 'Mark as sent via Email'}
                        className={`inline-flex items-center gap-1 px-1.5 py-1 rounded border transition-all ${inv.sent_via_email ? 'bg-emerald-100 border-emerald-400 text-emerald-800' : 'bg-white border-gray-300 text-gray-400 hover:border-gray-400'}`}
                        data-testid={`sent-email-${inv.id}`}
                      >
                        <EnvelopeSimple size={12} weight={inv.sent_via_email ? 'fill' : 'regular'} />
                        <span className="text-[9px] font-bold">{inv.sent_via_email ? 'SENT' : 'NO'}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => markSent(inv, 'whatsapp', !inv.sent_via_whatsapp)}
                        title={inv.sent_via_whatsapp ? `WhatsApp on ${(inv.whatsapp_sent_at || '').slice(0, 10)} — click to un-mark` : 'Mark as sent via WhatsApp'}
                        className={`inline-flex items-center gap-1 px-1.5 py-1 rounded border transition-all ${inv.sent_via_whatsapp ? 'bg-emerald-100 border-emerald-400 text-emerald-800' : 'bg-white border-gray-300 text-gray-400 hover:border-gray-400'}`}
                        data-testid={`sent-whatsapp-${inv.id}`}
                      >
                        <WhatsappLogo size={12} weight={inv.sent_via_whatsapp ? 'fill' : 'regular'} />
                        <span className="text-[9px] font-bold">{inv.sent_via_whatsapp ? 'SENT' : 'NO'}</span>
                      </button>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => downloadInvoicePdf(inv)} data-testid={`download-pdf-${inv.id}`} title="Download PDF">
                        <Download size={14} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => printInvoice(inv)} data-testid={`print-invoice-${inv.id}`} title="Print">
                        <Printer size={14} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openPayments(inv)} data-testid={`pay-invoice-${inv.id}`} title="Record Payment">
                        <CurrencyInr size={14} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onClone(inv)} data-testid={`clone-invoice-${inv.id}`} title="Clone as new draft">
                        <CopySimple size={14} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onEdit(inv)}><Pencil size={14} /></Button>
                      {inv.status === 'written_off' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-emerald-700 border-emerald-300"
                          onClick={() => restoreWriteOff(inv)}
                          data-testid={`restore-invoice-${inv.id}`}
                          title={`Restore — reason: ${inv.write_off_reason || 'other'}`}
                        >
                          <ArrowsClockwise size={14} />
                        </Button>
                      ) : inv.status !== 'paid' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-slate-700 border-slate-300"
                          onClick={() => openWriteOff(inv)}
                          data-testid={`writeoff-invoice-${inv.id}`}
                          title="Mark as written-off / bad debt (absconded client)"
                        >
                          <span className="text-[10px] font-bold tracking-wider">W-OFF</span>
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => onDel(inv.id)}><Trash size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
              });
            })()}
          </TableBody>
        </Table>

        {/* Filter totals — INR-equivalent across all matched invoices (uses each invoice's stored fx_rate_inr) */}
        <div
          className="px-4 py-3 grid grid-cols-1 sm:grid-cols-4 gap-3 text-sm border-t"
          style={{ background: 'linear-gradient(90deg,#f0fdf4 0%,#ecfdf5 100%)', borderColor: 'var(--dx-border)' }}
          data-testid="invoice-totals-strip"
        >
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">Invoices Matched</div>
            <div className="text-xl font-black text-emerald-900 mt-0.5">{filterTotals.count}</div>
            {filterTotals.mixed && (
              <div className="text-[10px] text-emerald-700 mt-0.5">Mixed: {filterTotals.currencies.join(', ')}</div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">Total Invoiced (INR)</div>
            <div className="text-xl font-black text-emerald-900 mt-0.5" data-testid="total-invoiced">
              ₹{filterTotals.invoiced_inr.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">Total Paid (INR)</div>
            <div className="text-xl font-black text-emerald-700 mt-0.5" data-testid="total-paid">
              ₹{filterTotals.paid_inr.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-rose-800 font-bold">Total Outstanding (INR)</div>
            <div className="text-xl font-black text-rose-700 mt-0.5" data-testid="total-outstanding">
              ₹{filterTotals.outstanding_inr.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        {/* Pagination footer */}
        <div
          className="px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap border-t bg-white"
          style={{ borderColor: 'var(--dx-border)' }}
          data-testid="invoice-pagination"
        >
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--dx-text2)' }}>
            <span>Show</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
              className="text-sm px-2 py-1.5 border rounded bg-white font-semibold"
              data-testid="invoice-page-size"
            >
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span data-testid="invoice-pagination-summary">
              {totalCount === 0
                ? 'Showing 0 results'
                : `Showing ${startIdx + 1} to ${endIdx} of ${totalCount} results`}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="px-2 py-1.5 text-sm border rounded bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              data-testid="invoice-page-prev"
              aria-label="Previous page"
            >‹</button>
            {(() => {
              // Compact page-number rendering: show first, last, current ±1, with ellipses
              const pages = [];
              const add = (v) => pages.push(v);
              const windowSize = 1;
              for (let p = 1; p <= totalPages; p += 1) {
                if (p === 1 || p === totalPages || (p >= safePage - windowSize && p <= safePage + windowSize)) {
                  add(p);
                } else if (pages[pages.length - 1] !== '…') {
                  add('…');
                }
              }
              return pages.map((p, i) => p === '…' ? (
                <span key={`e${i}`} className="px-1.5 text-gray-400 text-sm">…</span>
              ) : (
                <button
                  type="button"
                  key={p}
                  onClick={() => setPage(p)}
                  className={`min-w-[32px] px-2 py-1.5 text-sm border rounded font-semibold ${p === safePage ? 'text-white' : 'bg-white hover:bg-gray-50'}`}
                  style={p === safePage ? { background: 'var(--dx-primary)', borderColor: 'var(--dx-primary)' } : undefined}
                  data-testid={`invoice-page-${p}`}
                  aria-current={p === safePage ? 'page' : undefined}
                >{p}</button>
              ));
            })()}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="px-2 py-1.5 text-sm border rounded bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              data-testid="invoice-page-next"
              aria-label="Next page"
            >›</button>
          </div>
        </div>
      </Card>

      {/* Write-off Dialog — mark an invoice as absconded / bad debt */}
      <Dialog open={writeOffOpen} onOpenChange={(o) => { if (!writeOffBusy) setWriteOffOpen(o); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-slate-700">Write Off Invoice — {writeOffInv?.invoice_number}</span>
            </DialogTitle>
            <div className="text-xs text-gray-500 mt-1">
              The invoice will be hidden from the receivables listing and its outstanding will stop counting as owed. It stays in the DB for the Write-offs report.
            </div>
          </DialogHeader>
          {writeOffInv && (
            <div className="space-y-4 py-2">
              <div className="p-3 rounded bg-slate-50 text-xs space-y-1">
                <div><span className="text-gray-500">Client:</span> <b>{clientMap[writeOffInv.client_id]?.company_name || `${clientMap[writeOffInv.client_id]?.first_name || ''} ${clientMap[writeOffInv.client_id]?.last_name || ''}`.trim() || '(unlinked)'}</b></div>
                <div><span className="text-gray-500">Total:</span> <b>{writeOffInv.currency || 'INR'} {Number(writeOffInv.total || 0).toLocaleString()}</b> · <span className="text-gray-500">Paid:</span> {Number(writeOffInv.paid_amount || 0).toLocaleString()}</div>
                <div><span className="text-gray-500">Outstanding:</span> <b className="text-rose-700">{writeOffInv.currency || 'INR'} {Number((writeOffInv.total || 0) - (writeOffInv.paid_amount || 0)).toLocaleString()}</b></div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Reason</label>
                <select
                  value={writeOffReason}
                  onChange={(e) => setWriteOffReason(e.target.value)}
                  className="w-full mt-1 border rounded px-3 py-2 text-sm bg-white"
                  data-testid="writeoff-reason"
                >
                  <option value="absconded">🏃 Absconded — client is unreachable / not paying</option>
                  <option value="bad_debt">💸 Bad debt — recovery not viable</option>
                  <option value="disputed">⚖️ Disputed — legal / commercial dispute</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Note (optional)</label>
                <textarea
                  value={writeOffNote}
                  onChange={(e) => setWriteOffNote(e.target.value)}
                  placeholder="Any context you'd like to record — internal only, not sent to the client."
                  rows={3}
                  className="w-full mt-1 border rounded px-3 py-2 text-sm"
                  data-testid="writeoff-note"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button type="button" variant="outline" onClick={() => setWriteOffOpen(false)} disabled={writeOffBusy}>Cancel</Button>
                <Button
                  type="button"
                  onClick={submitWriteOff}
                  disabled={writeOffBusy}
                  className="text-white"
                  style={{ background: '#334155' }}
                  data-testid="writeoff-submit"
                >
                  {writeOffBusy ? 'Working…' : 'Confirm Write Off'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payments Dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Record Payment — {paymentsForInv?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {paymentsForInv && (
            <div className="space-y-4">
              {/* Invoice summary — Base + GST + Gross, plus Paid / Due */}
              {(() => {
                const base = invoiceTaxableBase(paymentsForInv);
                const gst = Number(paymentsForInv.tax_amount || 0);
                const gross = Number(paymentsForInv.total || 0);
                const paid = Number(paymentsForInv.paid_amount || 0);
                const due = Math.max(0, gross - paid);
                return (
                  <div className="grid grid-cols-5 gap-2 text-sm p-3 rounded-md" style={{ background: 'var(--dx-bg)' }} data-testid="payment-invoice-summary">
                    <div><div className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--dx-text2)' }}>Base (pre-GST)</div><div className="font-bold" data-testid="payment-base">{fmtCcy(base, paymentsForInv.currency)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--dx-text2)' }}>GST</div><div className="font-semibold">{fmtCcy(gst, paymentsForInv.currency)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--dx-text2)' }}>Gross Total</div><div className="font-bold" data-testid="payment-gross-total">{fmtCcy(gross, paymentsForInv.currency)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--dx-text2)' }}>Paid</div><div className="font-bold" style={{ color: '#0F6E56' }}>{fmtCcy(paid, paymentsForInv.currency)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--dx-text2)' }}>Due</div><div className="font-bold" style={{ color: '#A32D2D' }} data-testid="payment-due">{fmtCcy(due, paymentsForInv.currency)}</div></div>
                  </div>
                );
              })()}

              <form onSubmit={submitPayment} className="grid grid-cols-2 gap-3 items-end">
                {/* Foreign-currency invoices: dedicated INR conversion panel so admin can
                    reconcile SGD/USD invoiced vs. INR actually received in the bank. */}
                {paymentsForInv.currency && paymentsForInv.currency !== 'INR' && (() => {
                  const rate = Number(paymentsForInv.fx_rate_inr || 1) || 1;
                  const grossFx = parseFloat(payForm.received_amount) || 0;
                  const tdsPct = parseFloat(payForm.tds_percentage) || 0;
                  const base = invoiceTaxableBase(paymentsForInv);
                  const tdsAmt = tdsPct > 0 ? Math.round(base * tdsPct) / 100 : 0;
                  const netFx = Math.max(0, grossFx - tdsAmt);
                  const expectedInr = Math.round(netFx * rate * 100) / 100;
                  const actualInr = parseFloat(payForm.inr_received) || 0;
                  const delta = Math.round((actualInr - expectedInr) * 100) / 100;
                  const deltaAbs = Math.abs(delta);
                  return (
                    <div className="col-span-2 border-2 border-indigo-200 rounded-lg p-3 bg-indigo-50/40" data-testid="payment-fx-panel">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-800">
                          Foreign Currency Conversion ({paymentsForInv.currency} → INR)
                        </div>
                        <div className="text-[10px] text-indigo-600 font-mono">
                          Invoice rate: 1 {paymentsForInv.currency} = ₹{rate.toFixed(4)}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div>
                          <Label className="text-[10px] uppercase font-bold text-gray-600">Expected INR at Invoice Rate</Label>
                          <div className="mt-1 font-bold text-lg text-indigo-800 tabular-nums" data-testid="payment-expected-inr">
                            ₹{expectedInr.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </div>
                          <div className="text-[10px] text-gray-500 mt-0.5">{fmtCcy(netFx, paymentsForInv.currency)} × {rate.toFixed(4)}</div>
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase font-bold text-gray-600">INR Received in Bank *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={payForm.inr_received}
                            onChange={(e) => setPayForm({ ...payForm, inr_received: e.target.value })}
                            className="mt-1 font-semibold"
                            placeholder="Actual amount credited"
                            data-testid="payment-inr-received"
                          />
                          <div className="text-[10px] text-gray-500 mt-0.5">What actually landed in ₹</div>
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase font-bold text-gray-600">FX Difference</Label>
                          <div className={`mt-1 font-bold text-lg tabular-nums ${delta > 0 ? 'text-emerald-700' : delta < 0 ? 'text-rose-700' : 'text-gray-500'}`} data-testid="payment-fx-delta">
                            {delta > 0 ? '+₹' : delta < 0 ? '−₹' : '₹'}{deltaAbs.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </div>
                          <div className={`text-[10px] mt-0.5 font-semibold ${delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-rose-600' : 'text-gray-500'}`}>
                            {delta > 0 ? '↑ FX gain' : delta < 0 ? '↓ FX loss' : 'no variance'}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 text-[10px] text-indigo-700 bg-white/60 px-2 py-1 rounded">
                        {payForm.paid_account_id
                          ? <>ℹ The bank ledger will be credited with the actual INR amount (<span className="font-semibold">₹{actualInr.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>) so your bank balance reconciles exactly.</>
                          : <>⚠ <span className="font-semibold">No bank account linked</span> — the invoice will be marked paid ({paymentsForInv.currency} {grossFx}) but the ₹ amount is <span className="font-semibold">not posted to any ledger</span>. Pick an account below to reconcile.</>}
                      </div>
                    </div>
                  );
                })()}

                <div>
                  <Label>
                    Amount Received{paymentsForInv.currency && paymentsForInv.currency !== 'INR' ? ` (in ${paymentsForInv.currency})` : ''} *
                  </Label>
                  <Input required type="number" step="0.01" value={payForm.received_amount}
                    onChange={(e) => setPayForm({ ...payForm, received_amount: e.target.value })} data-testid="payment-received-amount" />
                  <p className="text-[10px] mt-1" style={{ color: 'var(--dx-text2)' }}>
                    {paymentsForInv.currency && paymentsForInv.currency !== 'INR'
                      ? `Invoice-currency amount (${paymentsForInv.currency}) that reduces the invoice due. This is NOT the INR figure — enter the ₹ actually received in the panel above.`
                      : 'What actually hit your bank (Gross − TDS).'}
                  </p>
                </div>
                <div>
                  <Label>Date *</Label>
                  <Input required type="date" value={payForm.paid_date}
                    onChange={(e) => setPayForm({ ...payForm, paid_date: e.target.value })} data-testid="payment-date" />
                </div>
                <div>
                  <Label>Paid Via *</Label>
                  <Select value={payForm.paid_via} onValueChange={(v) => setPayForm({ ...payForm, paid_via: v })}>
                    <SelectTrigger data-testid="payment-paid-via"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank">🏦 Bank Transfer (NEFT/IMPS/RTGS)</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="gpay">Google Pay</SelectItem>
                      <SelectItem value="phonepe">PhonePe</SelectItem>
                      <SelectItem value="paytm">Paytm</SelectItem>
                      <SelectItem value="cash">💵 Cash</SelectItem>
                      <SelectItem value="cheque">📝 Cheque</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>To Account</Label>
                  <Select
                    value={payForm.paid_account_id || '__free'}
                    onValueChange={(v) => setPayForm({ ...payForm, paid_account_id: v === '__free' ? '' : v })}
                  >
                    <SelectTrigger data-testid="payment-account"><SelectValue placeholder="Pick an account…" /></SelectTrigger>
                    <SelectContent>
                      {bankAccounts.filter((a) => a.is_active).map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.nickname} — ₹{Number(a.balance || 0).toLocaleString('en-IN')}
                        </SelectItem>
                      ))}
                      <SelectItem value="__free">— Not linked (free-text only) —</SelectItem>
                    </SelectContent>
                  </Select>
                  {!payForm.paid_account_id && (
                    <div className="mt-1.5 text-[10px] leading-snug px-2 py-1.5 rounded bg-amber-50 border border-amber-200 text-amber-800" data-testid="payment-no-account-warning">
                      ⚠ <span className="font-semibold">No bank account selected.</span> The receipt will still be recorded against the invoice ({paymentsForInv.currency && paymentsForInv.currency !== 'INR' ? `${paymentsForInv.currency} amount reduces the due` : 'INR amount reduces the due'}), but <span className="font-semibold">no INR ledger entry</span> will be created — your bank balance will not update. Select an account to reconcile.
                    </div>
                  )}
                </div>

                {/* TDS Block */}
                <div className="col-span-2 border rounded-md p-3 bg-amber-50/50 border-amber-200">
                  <div className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-2">
                    TDS Deducted by Client (calculated on BASE / pre-GST amount)
                  </div>
                  <div className="grid grid-cols-3 gap-3 items-end">
                    <div>
                      <Label className="text-xs">TDS %</Label>
                      <Select
                        value={String(payForm.tds_percentage || 0)}
                        onValueChange={(v) => {
                          const pct = v === 'custom' ? payForm.tds_percentage : parseFloat(v);
                          const base = invoiceTaxableBase(paymentsForInv);
                          const newTds = pct > 0 ? Math.round(base * pct) / 100 : 0;
                          const gross = Number(paymentsForInv.total || 0);
                          const paid = Number(paymentsForInv.paid_amount || 0);
                          const outstanding = Math.max(0, gross - paid);
                          // Auto-adjust received_amount so gross_settled = outstanding
                          setPayForm({ ...payForm, tds_percentage: pct, received_amount: Math.max(0, outstanding - newTds) });
                        }}
                      >
                        <SelectTrigger data-testid="payment-tds-pct"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">No TDS</SelectItem>
                          <SelectItem value="1">1% (194Q goods)</SelectItem>
                          <SelectItem value="2">2% (194C contracts)</SelectItem>
                          <SelectItem value="5">5% (194I rent / 194H comm.)</SelectItem>
                          <SelectItem value="10">10% (194J professional)</SelectItem>
                          <SelectItem value="custom">Custom…</SelectItem>
                        </SelectContent>
                      </Select>
                      {!([0, 1, 2, 5, 10].includes(Number(payForm.tds_percentage))) && (
                        <Input
                          type="number" step="0.01" min="0" max="100"
                          className="mt-1.5 h-8"
                          placeholder="Custom %"
                          value={payForm.tds_percentage}
                          onChange={(e) => {
                            const pct = parseFloat(e.target.value) || 0;
                            const base = invoiceTaxableBase(paymentsForInv);
                            const newTds = pct > 0 ? Math.round(base * pct) / 100 : 0;
                            const gross = Number(paymentsForInv.total || 0);
                            const paid = Number(paymentsForInv.paid_amount || 0);
                            const outstanding = Math.max(0, gross - paid);
                            setPayForm({ ...payForm, tds_percentage: pct, received_amount: Math.max(0, outstanding - newTds) });
                          }}
                          data-testid="payment-tds-custom"
                        />
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">Section</Label>
                      <Select value={payForm.tds_section || '__none'} onValueChange={(v) => setPayForm({ ...payForm, tds_section: v === '__none' ? '' : v })}>
                        <SelectTrigger data-testid="payment-tds-section"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">— None —</SelectItem>
                          <SelectItem value="194C">194C — Contractor</SelectItem>
                          <SelectItem value="194J">194J — Professional</SelectItem>
                          <SelectItem value="194H">194H — Commission</SelectItem>
                          <SelectItem value="194I">194I — Rent</SelectItem>
                          <SelectItem value="194Q">194Q — Goods</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-xs space-y-0.5 bg-white border rounded p-2">
                      {(() => {
                        const base = invoiceTaxableBase(paymentsForInv);
                        const pct = parseFloat(payForm.tds_percentage) || 0;
                        const received = parseFloat(payForm.received_amount) || 0;
                        const tds = pct > 0 ? Math.round(base * pct) / 100 : 0;
                        const grossSettled = Math.round((received + tds) * 100) / 100;
                        const ccy = paymentsForInv.currency;
                        return (
                          <>
                            <div className="flex justify-between"><span className="text-gray-600">Base × {pct}%</span><span className="font-semibold text-amber-700" data-testid="payment-tds-calc">{fmtCcy(tds, ccy)}</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">+ Received</span><span className="font-semibold text-emerald-700" data-testid="payment-net-amount">{fmtCcy(received, ccy)}</span></div>
                            <div className="flex justify-between border-t pt-0.5 mt-0.5"><span className="text-gray-700">= Settles</span><span className="font-bold" data-testid="payment-gross-settled">{fmtCcy(grossSettled, ccy)}</span></div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Reference</Label>
                  <Input
                    value={payForm.paid_reference}
                    placeholder="UTR / Cheque #"
                    onChange={(e) => setPayForm({ ...payForm, paid_reference: e.target.value })}
                    data-testid="payment-reference"
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input
                    value={payForm.paid_notes}
                    onChange={(e) => setPayForm({ ...payForm, paid_notes: e.target.value })}
                    data-testid="payment-notes"
                  />
                </div>
                <div className="col-span-2 flex justify-end">
                  <Button type="submit" className="text-white" style={{ background: 'var(--dx-primary)' }} data-testid="save-payment-btn">
                    Add Payment
                  </Button>
                </div>
              </form>

              <div>
                <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--dx-text2)' }}>
                  Payment History
                </div>
                {invoicePayments.length === 0 ? (
                  <div className="text-sm py-4 text-center" style={{ color: 'var(--dx-text3)' }}>No payments yet.</div>
                ) : (
                  <Table>
                    <TableHeader><TableRow style={{ background: 'var(--dx-bg)' }}>
                      <TableHead>DATE</TableHead><TableHead>VIA</TableHead><TableHead>REF</TableHead>
                      <TableHead className="text-right">GROSS</TableHead>
                      <TableHead className="text-right">TDS</TableHead>
                      <TableHead className="text-right">NET</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {invoicePayments.map((p) => {
                        const gross = Number(p.amount || 0);
                        const tds = Number(p.tds_amount || 0);
                        const net = Number(p.net_amount != null ? p.net_amount : (gross - tds));
                        return (
                        <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                          <TableCell>{toDDMMYYYY(p.paid_date || p.payment_date)}</TableCell>
                          <TableCell className="capitalize">{p.paid_via || p.mode || '-'}</TableCell>
                          <TableCell className="text-xs">{p.paid_reference || p.reference || '-'}</TableCell>
                          <TableCell className="text-right font-semibold">{fmtCcy(gross, paymentsForInv.currency)}</TableCell>
                          <TableCell className="text-right text-xs">
                            {tds > 0 ? (
                              <span className="text-amber-700 font-semibold" data-testid={`payment-row-tds-${p.id}`}>
                                {fmtCcy(tds, paymentsForInv.currency)}
                                {p.tds_section ? <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-100">{p.tds_section}</span> : null}
                              </span>
                            ) : <span className="text-gray-300">—</span>}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-emerald-700">
                            {fmtCcy(net, paymentsForInv.currency)}
                            {/* Foreign currency: show INR received + FX gain/loss */}
                            {p.invoice_currency && p.invoice_currency !== 'INR' && p.inr_received != null && (
                              <div className="text-[10px] font-normal mt-0.5">
                                <span className="text-gray-600">= ₹{Number(p.inr_received).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                                {Math.abs(Number(p.fx_gain_loss || 0)) >= 0.01 && (
                                  <span className={`ml-1 font-bold ${Number(p.fx_gain_loss) > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                    ({Number(p.fx_gain_loss) > 0 ? '+' : '−'}₹{Math.abs(Number(p.fx_gain_loss)).toLocaleString('en-IN', { maximumFractionDigits: 2 })})
                                  </span>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => deletePayment(p.id)}>
                              <Trash size={12} />
                            </Button>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesPage;
